/**
 * Email templates for super-admin 1-to-1 customer outreach.
 *
 * Each template renders against a `customer` object. Variable substitution
 * is plain {{token}} replacement — keeps it readable in the UI. Operators
 * are expected to edit the rendered output before sending; templates are a
 * starting point, not a script.
 *
 * Add new templates here and they automatically appear in the dropdown of
 * any page using <SendDirectEmailModal>.
 */

function firstNameOf(customer) {
  const name = (customer?.name || '').trim();
  if (name) return name.split(/\s+/)[0];
  if (customer?.email) return customer.email.split('@')[0];
  return 'there';
}

function inDeliveryRegion(customer) {
  const state = (customer?.address?.state || '').toUpperCase();
  return ['NJ', 'DC', 'MD', 'DMV'].includes(state);
}

function render(template, customer, extra = {}) {
  const tokens = {
    first_name: firstNameOf(customer),
    full_name: customer?.name || firstNameOf(customer),
    city: customer?.address?.city || 'your area',
    state: customer?.address?.state || '',
    order_id: extra.order_id || '',
    order_total: extra.order_total != null ? `$${Number(extra.order_total).toFixed(2)}` : '',
    refund_amount: extra.refund_amount != null ? `$${Number(extra.refund_amount).toFixed(2)}` : '',
    days_quiet: extra.days_quiet || '14',
  };
  const swap = (s) => s.replace(/\{\{(\w+)\}\}/g, (_, k) => tokens[k] || '');
  return { subject: swap(template.subject), body_text: swap(template.body_text) };
}

// --- Template definitions ----------------------------------------------------

const TPL_APOLOGY_BUG = {
  id: 'apology_bug',
  label: '🩹 Apology — recent app bug',
  subject: 'Quick note about your Ash & Merch order',
  body_text:
`Hey {{first_name}} —

I caught a bug today that was silently blocking Place Order for a couple of new signups, yours included. Just fixed it — try again whenever you're ready.

As an apology for the friction, I dropped 100 points on your account (10% off your next order).

Let me know if you hit any other snags.

Brian — Ash & Merch`,
};

const TPL_OUT_OF_REGION = {
  id: 'out_of_region',
  label: '📍 Out of delivery zone',
  subject: 'Heads up on Ash & Merch delivery zones',
  body_text:
`Hey {{first_name}} —

Brian from Ash & Merch. I saw your address is in {{city}} — we're not delivering there yet (currently NJ / DC / MD only).

I'll keep you on the list and ping you the moment we expand. Appreciate you signing up.

Brian — Ash & Merch`,
};

const TPL_REFUND_CONFIRM = {
  id: 'refund_confirm',
  label: '💸 Refund confirmation',
  subject: 'Your Ash & Merch refund is on the way',
  body_text:
`Hey {{first_name}} —

Quick confirmation: we refunded {{refund_amount}} for order {{order_id}}. The credit should hit your card in 3-5 business days, depending on your bank.

Thanks for being patient — and for ordering with us. If anything else seems off, just reply.

Brian — Ash & Merch`,
};

const TPL_DELIVERY_FAILED = {
  id: 'delivery_failed',
  label: '🚚 Failed delivery + reschedule',
  subject: 'About your delivery today',
  body_text:
`Hey {{first_name}} —

We couldn't complete your delivery today (order {{order_id}}). Sometimes it's a wrong unit number, no answer at the door, or our driver couldn't get to a safe drop-off.

Reply back with the best time tomorrow and I'll personally get it rescheduled. No restock fee, no hassle.

Brian — Ash & Merch`,
};

const TPL_QUIET_NUDGE = {
  id: 'quiet_nudge',
  label: '👋 "We noticed you\'ve been quiet"',
  subject: 'Anything you need from us, {{first_name}}?',
  body_text:
`Hey {{first_name}} —

Noticed you haven't ordered in about {{days_quiet}} days. Not a pitch — just want to make sure nothing went sideways last time.

If something didn't land right or you can't find what you're looking for, hit reply and tell me. I read every one.

Brian — Ash & Merch`,
};

const TPL_RECOVERY = {
  id: 'recovery',
  label: '🛟 Negative satisfaction recovery',
  subject: 'I want to make this right',
  body_text:
`Hey {{first_name}} —

Saw your feedback come through and I take it seriously. Whatever fell short, I'd like to make it right.

Reply back with what happened in your own words — I'd rather hear it from you directly than guess. I'll personally sort it.

Brian — Ash & Merch`,
};

const TPL_PAYMENT_RETRY = {
  id: 'payment_retry',
  label: '💳 Payment failed — try again',
  subject: 'Your Ash & Merch order didn\'t go through',
  body_text:
`Hey {{first_name}} —

Heads up: your last checkout attempt didn't complete — looks like a payment hiccup, not anything on your end. Your cart {{order_total}} is still saved.

When you get a sec, try again here: https://ashandmerch.com/customer/cart

If it bounces a second time, reply and I'll sort it manually.

Brian — Ash & Merch`,
};

export const EMAIL_TEMPLATES = [
  TPL_APOLOGY_BUG,
  TPL_OUT_OF_REGION,
  TPL_REFUND_CONFIRM,
  TPL_DELIVERY_FAILED,
  TPL_QUIET_NUDGE,
  TPL_RECOVERY,
  TPL_PAYMENT_RETRY,
];

/**
 * Pick the best default template for a customer based on context.
 * Used as the modal's initial selection so the operator usually doesn't
 * need to change it. Always overridable in the UI.
 */
export function defaultTemplateFor(customer, context = {}) {
  if (context.template_id) {
    const found = EMAIL_TEMPLATES.find((t) => t.id === context.template_id);
    if (found) return render(found, customer, context);
  }
  if (context.refund_amount != null) return render(TPL_REFUND_CONFIRM, customer, context);
  if (context.payment_failed) return render(TPL_PAYMENT_RETRY, customer, context);
  if (context.delivery_failed) return render(TPL_DELIVERY_FAILED, customer, context);
  if (!inDeliveryRegion(customer)) return render(TPL_OUT_OF_REGION, customer, context);
  return render(TPL_APOLOGY_BUG, customer, context);
}

export function renderTemplate(templateId, customer, context = {}) {
  const tpl = EMAIL_TEMPLATES.find((t) => t.id === templateId);
  if (!tpl) return defaultTemplateFor(customer, context);
  return render(tpl, customer, context);
}
